import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  MenuDigitizationServiceInterface,
  MenuExtractionJob,
  MenuExtractionJobDetail,
  JobsPagedResult,
  ExtractedItemsPagedResult,
  ExtractedMenuItem,
  ExtractedMenuCategory,
  GetJobsParams,
  GetExtractedItemsParams,
  UpdateExtractedItemParams,
  UpdateExtractedCategoryParams,
  ApproveItemParams,
  ApproveCategoryParams,
  BulkApproveParams,
  ApproveItemResponse,
  ApproveCategoryResponse,
  BulkApproveResponse,
} from "../types/menuDigitization.types";

const BASE_URL = "/api/admin/menu-digitization";

export class MenuDigitizationService
  extends BaseService
  implements MenuDigitizationServiceInterface
{
  async uploadFile(file: File): Promise<ApiResult<MenuExtractionJob>> {
    const formData = new FormData();
    formData.append("file", file);

    return this.handleRequest<MenuExtractionJob>(
      this.client.post(`${BASE_URL}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
    );
  }

  async submitText(text: string): Promise<ApiResult<MenuExtractionJob>> {
    return this.handleRequest<MenuExtractionJob>(
      this.client.post(`${BASE_URL}/text`, { text })
    );
  }

  async getJobs(params?: GetJobsParams): Promise<ApiResult<JobsPagedResult>> {
    const queryParams = this.buildQueryParams({
      pageNumber: params?.pageNumber ?? 1,
      pageSize: params?.pageSize ?? 10,
      status: params?.status,
    });

    return this.handleRequest<JobsPagedResult>(
      this.client.get(`${BASE_URL}/jobs`, { params: queryParams })
    );
  }

  async getJobDetail(
    jobId: string
  ): Promise<ApiResult<MenuExtractionJobDetail>> {
    return this.handleRequest<MenuExtractionJobDetail>(
      this.client.get(`${BASE_URL}/jobs/${jobId}`)
    );
  }

  async deleteJob(jobId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`${BASE_URL}/jobs/${jobId}`)
    );
  }

  async retryJob(jobId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`${BASE_URL}/jobs/${jobId}/retry`)
    );
  }

  async getExtractedItems(
    params: GetExtractedItemsParams
  ): Promise<ApiResult<ExtractedItemsPagedResult>> {
    const queryParams = this.buildQueryParams({
      pageNumber: params.pageNumber ?? 1,
      pageSize: params.pageSize ?? 20,
      status: params.status,
      minConfidence: params.minConfidence,
    });

    return this.handleRequest<ExtractedItemsPagedResult>(
      this.client.get(`${BASE_URL}/jobs/${params.jobId}/items`, {
        params: queryParams,
      })
    );
  }

  async getExtractedItem(
    itemId: string
  ): Promise<ApiResult<ExtractedMenuItem>> {
    return this.handleRequest<ExtractedMenuItem>(
      this.client.get(`${BASE_URL}/items/${itemId}`)
    );
  }

  async updateExtractedItem(
    params: UpdateExtractedItemParams
  ): Promise<ApiResult<void>> {
    const { itemId, ...updateData } = params;
    return this.handleRequest<void>(
      this.client.put(`${BASE_URL}/items/${itemId}`, updateData)
    );
  }

  async getExtractedCategories(
    jobId: string
  ): Promise<ApiResult<ExtractedMenuCategory[]>> {
    return this.handleRequest<ExtractedMenuCategory[]>(
      this.client.get(`${BASE_URL}/jobs/${jobId}/categories`)
    );
  }

  async updateExtractedCategory(
    params: UpdateExtractedCategoryParams
  ): Promise<ApiResult<void>> {
    const { categoryId, ...updateData } = params;
    return this.handleRequest<void>(
      this.client.put(`${BASE_URL}/categories/${categoryId}`, updateData)
    );
  }

  async approveItem(
    params: ApproveItemParams
  ): Promise<ApiResult<ApproveItemResponse>> {
    const { itemId, ...approveData } = params;
    return this.handleRequest<ApproveItemResponse>(
      this.client.post(`${BASE_URL}/items/${itemId}/approve`, approveData)
    );
  }

  async rejectItem(itemId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`${BASE_URL}/items/${itemId}/reject`)
    );
  }

  async approveCategory(
    params: ApproveCategoryParams
  ): Promise<ApiResult<ApproveCategoryResponse>> {
    const { categoryId, ...approveData } = params;
    return this.handleRequest<ApproveCategoryResponse>(
      this.client.post(`${BASE_URL}/categories/${categoryId}/approve`, approveData)
    );
  }

  async rejectCategory(categoryId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`${BASE_URL}/categories/${categoryId}/reject`)
    );
  }

  async bulkApproveItems(
    params: BulkApproveParams
  ): Promise<ApiResult<BulkApproveResponse>> {
    const { jobId, ...bulkData } = params;
    return this.handleRequest<BulkApproveResponse>(
      this.client.post(`${BASE_URL}/jobs/${jobId}/bulk-approve`, bulkData)
    );
  }
}
